/**
 * Overlay + sizing math for the 0DTE underlying price chart card.
 *
 * Kept separate from the card component so the arithmetic that has to agree with
 * the Strike Positioning preview (and with the strike band the backend actually
 * receives) is testable on its own.
 */

// Indicators that render as their own stacked subpanel inside
// AdvancedPriceChartWidget — each one takes height away from the price panel.
export const SUBPANEL_INDICATORS = ['macd', 'rsi', 'volume', 'ac'];

const BASE_HEIGHT = 420;      // ≈ the Market Direction card, a little taller
const SUBPANEL_HEIGHT = 95;   // added per stacked subpanel
const MIN_HEIGHT = 280;       // below this the price panel stops being readable
const CHROME_ALLOWANCE = 260; // card header + hint + padding + breathing room

// Height of the chart's hover-value strip, matching its min-h in the widget.
export const VALUE_HEADER_HEIGHT = 26;

/**
 * Horizontal markers for the price panel.
 *
 * The target line only appears in target-price mode — in bands mode there is no
 * target to draw. The buffer band edges are a fraction of the TARGET (not of
 * spot), matching TargetPriceStrikeSection's preview and the strike_low_pct /
 * strike_high_pct that buildZeroDTETargetScenarios sends to the backend.
 *
 * @param {Object} params - { strikeMode, targetPrice, bufferPct, currentPrice }
 * @returns {{lines: Array, bands: Array}} ready to pass to AdvancedPriceChartWidget
 */
export function buildTargetOverlays({ strikeMode, targetPrice, bufferPct, currentPrice } = {}) {
  const target = parseFloat(targetPrice);
  const spot = parseFloat(currentPrice);
  const hasTarget = strikeMode === 'target' && Number.isFinite(target) && target > 0;

  const lines = [];
  if (hasTarget) {
    lines.push({
      value: target,
      label: `Target $${target.toFixed(2)}`,
      color: '#218aff',
      strokeDasharray: '4 4',
    });
  }
  if (Number.isFinite(spot) && spot > 0) {
    lines.push({
      value: spot,
      label: 'Spot',
      color: '#9ca3af',
      strokeDasharray: '2 4',
    });
  }

  const buffer = parseFloat(bufferPct);
  const bands = hasTarget && Number.isFinite(buffer) && buffer > 0
    ? [{
        from: target * (1 - buffer),
        to: target * (1 + buffer),
        fill: '#218aff',
        fillOpacity: 0.12,
      }]
    : [];

  return { lines, bands };
}

/**
 * Chart height in px. Grows with each stacked subpanel the user enables, and is
 * clamped against the viewport so the sticky card can never run off screen —
 * which is why the card itself needs no max-height (a max-height would fight the
 * widget's explicit height and clip it instead).
 *
 * @param {string[]} indicators - currently selected indicator keys
 * @param {number} viewportHeight - window.innerHeight
 * @param {Object} [opts]
 * @param {number} [opts.valueHeaderPx=0] - extra height for the hover-value
 *   strip, so adding it doesn't eat into the price panel
 * @returns {number} height in px
 */
export function computeChartHeight(indicators = [], viewportHeight = 900, opts = {}) {
  const { valueHeaderPx = 0 } = opts;
  const subpanels = (Array.isArray(indicators) ? indicators : [])
    .filter(i => SUBPANEL_INDICATORS.includes(i)).length;
  const desired = BASE_HEIGHT + subpanels * SUBPANEL_HEIGHT + valueHeaderPx;
  const viewportCap = (Number(viewportHeight) || 900) - CHROME_ALLOWANCE;
  return Math.max(MIN_HEIGHT, Math.min(desired, viewportCap));
}
